/**
 * Service de chargement des configurations JSON
 * Responsable du chargement typé des fichiers de configuration
 */

import type {
  GradeListConfig,
  ItemConfig,
  MaterialConfig,
  FeatureConfig,
  CombinationConfig,
  GradeItem
} from '../types';

/**
 * Types d'erreurs personnalisés pour le chargement
 */
export class ConfigLoaderError extends Error {
  constructor(
    message: string,
    public readonly type: 'network' | 'parse' | 'not_found' | 'invalid_data',
    public readonly path?: string
  ) {
    super(message);
    this.name = 'ConfigLoaderError';
  }
}

/**
 * Interface pour les données de configuration d'un grade
 */
export interface GradeConfigData {
  items: ItemConfig;
  materials: MaterialConfig;
  features: FeatureConfig;
  combinations: CombinationConfig;
}

/**
 * Service ConfigLoader - Singleton pour le chargement des configurations
 */
export class ConfigLoader {
  private static instance: ConfigLoader;
  private cache: Map<string, any> = new Map();
  private readonly enableCache: boolean;

  private constructor(enableCache: boolean = true) {
    this.enableCache = enableCache;
  }

  /**
   * Obtient l'instance singleton du ConfigLoader
   */
  public static getInstance(): ConfigLoader {
    if (!ConfigLoader.instance) {
      ConfigLoader.instance = new ConfigLoader();
    }
    return ConfigLoader.instance;
  }

  /**
   * Charge la liste des grades disponibles
   */
  public async loadGradeList(): Promise<GradeListConfig> {
    const path = `${import.meta.env.BASE_URL}assets/setting/grade_list.json`;
    return this.loadJsonFile<GradeListConfig>(path);
  }

  /**
   * Charge la configuration complète d'un grade spécifique
   */
  public async loadGradeConfig(gradeId: string): Promise<GradeConfigData> {
    // D'abord, charger la liste des grades pour obtenir les chemins
    const gradeList = await this.loadGradeList();

    // Trouver le grade demandé
    const grade = this.findGradeById(gradeList, gradeId);
    if (!grade) {
      throw new ConfigLoaderError(
        `Grade avec l'ID "${gradeId}" non trouvé`,
        'not_found',
        gradeId
      );
    }

    // Charger tous les fichiers de configuration du grade
    // Les chemins dans grade_list.json sont relatifs à assets/setting/
    const basePath = `${import.meta.env.BASE_URL}assets/setting/`;
    const [items, materials, features, combinations] = await Promise.all([
      this.loadJsonFile<ItemConfig>(basePath + grade.files.items),
      this.loadJsonFile<MaterialConfig>(basePath + grade.files.materials),
      this.loadJsonFile<FeatureConfig>(basePath + grade.files.features),
      this.loadJsonFile<CombinationConfig>(basePath + grade.files.combinations)
    ]);

    return {
      items,
      materials,
      features,
      combinations
    };
  }

  /**
   * Charge un fichier JSON générique avec typage
   */
  public async loadJsonFile<T>(path: string): Promise<T> {
    // Vérifier le cache si activé
    if (this.enableCache && this.cache.has(path)) {
      return this.cache.get(path) as T;
    }

    try {
      const response = await fetch(path);

      if (!response.ok) {
        throw new ConfigLoaderError(
          `Erreur HTTP ${response.status}: ${response.statusText}`,
          'network',
          path
        );
      }

      const data = await response.json();

      // Validation basique des données
      if (!data || typeof data !== 'object') {
        throw new ConfigLoaderError(
          'Données JSON invalides ou vides',
          'invalid_data',
          path
        );
      }

      // Mettre en cache si activé
      if (this.enableCache) {
        this.cache.set(path, data);
      }

      return data as T;

    } catch (error) {
      if (error instanceof ConfigLoaderError) {
        throw error;
      }

      // Erreur de parsing JSON
      if (error instanceof SyntaxError) {
        throw new ConfigLoaderError(
          'Erreur de parsing JSON',
          'parse',
          path
        );
      }

      // Erreur réseau générique
      throw new ConfigLoaderError(
        `Erreur lors du chargement du fichier: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        'network',
        path
      );
    }
  }

  /**
   * Vide le cache des fichiers chargés
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Désactive le cache pour les chargements futurs
   */
  public disableCache(): void {
    (this as any).enableCache = false;
    this.clearCache();
  }

  /**
   * Réactive le cache
   */
  public enableCaching(): void {
    (this as any).enableCache = true;
  }

  /**
   * Trouve un grade par son ID dans la liste des grades
   */
  private findGradeById(gradeList: GradeListConfig, gradeId: string): GradeItem | null {
    for (const group of gradeList.GRADE.groups) {
      const grade = group.items.find(item => item.item_id === gradeId);
      if (grade) {
        return grade;
      }
    }
    return null;
  }
}

// Export de l'instance singleton par défaut
export const configLoader = ConfigLoader.getInstance();